import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Loader2, AlertCircle } from 'lucide-react';
import PublicHeader from '../../components/ui/PublicHeader';
import newsletterService from '../../services/newsletterService';

import formatNewsletterContent from '../../utils/formatNewsletterContent';

const NewsletterDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (slug) {
      loadNewsletter();
    }
  }, [slug]);

  const loadNewsletter = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await newsletterService?.getBySlug(slug);
      setNewsletter(data);
    } catch (err) {
      setError(err?.message || 'Newsletter not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading newsletter...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900">Newsletter Not Found</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => navigate('/newsletter-archive')}
                  className="mt-4 text-sm font-medium text-red-900 hover:text-red-700"
                >
                  ← Back to Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/newsletter-archive')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Archive</span>
          </button>

          <article className="bg-card border border-border rounded-lg shadow-elevation-sm p-8">
            <header className="mb-8 border-b border-border pb-6">
              <h1 className="text-4xl font-heading font-bold text-foreground mb-4">
                {newsletter?.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(newsletter?.publishedAt)}</span>
                </div>
              </div>

              {newsletter?.tags?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Tag className="w-4 h-4 text-muted-foreground" />
                  {newsletter?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Scoped inline styles for compact spacing */}
            <style>
              {`
                /* Override Tailwind Typography prose spacing with maximum specificity */
                .newsletter-content.prose p,
                .newsletter-content.prose-lg p {
                  margin-top: 0 !important;
                  margin-bottom: 0.5em !important;
                  line-height: 1.6 !important;
                }
                
                .newsletter-content.prose > * + *,
                .newsletter-content.prose-lg > * + * {
                  margin-top: 0.5em !important;
                }
                
                .newsletter-content.prose ul,
                .newsletter-content.prose-lg ul,
                .newsletter-content.prose ol,
                .newsletter-content.prose-lg ol {
                  margin-top: 0.5em !important;
                  margin-bottom: 0.5em !important;
                  padding-left: 1.5em !important;
                }
                
                .newsletter-content.prose li,
                .newsletter-content.prose-lg li {
                  margin-top: 0 !important;
                  margin-bottom: 0.25em !important;
                  line-height: 1.6 !important;
                }
                
                .newsletter-content.prose li > p,
                .newsletter-content.prose-lg li > p {
                  margin-top: 0 !important;
                  margin-bottom: 0 !important;
                }
                
                .newsletter-content.prose h1,
                .newsletter-content.prose-lg h1,
                .newsletter-content.prose h2,
                .newsletter-content.prose-lg h2,
                .newsletter-content.prose h3,
                .newsletter-content.prose-lg h3,
                .newsletter-content.prose h4,
                .newsletter-content.prose-lg h4 {
                  margin-top: 1em !important;
                  margin-bottom: 0.5em !important;
                  line-height: 1.3 !important;
                }
                
                /* First element should have no top margin */
                .newsletter-content.prose > *:first-child,
                .newsletter-content.prose-lg > *:first-child {
                  margin-top: 0 !important;
                }
              `}
            </style>

            <div 
              className="newsletter-content prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: formatNewsletterContent(newsletter?.content) 
              }}
            />
          </article>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/newsletter-archive')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              View All Newsletters
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewsletterDetailPage;